import { useStencilContext } from "@amzn/stencil-react-components/context";
import { Container, Spacer } from "@amzn/stencil-react-components/layout";
import { PageContainer } from "@amzn/stencil-react-components/page";
import { H1 } from "@amzn/stencil-react-components/text";
import { useContext, useEffect } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { ChatbotFlyout } from "@/components/Chatbot/ChatbotFlyout";
import { ChatSupportFlyout, GetSupportFlyout } from "@/components/GetSupport";
import HomeContent from "@/components/HomeContent/HomeContent";
import OnboardingPageLayout from "@/components/OnboardingPageLayout";
import ProgressRail from "@/components/ProgressRail";
import { useSelectedLanguage } from "@/hooks/useSelectedLanguage";
import { RTL_LANGUAGES } from "@/i18n/locales";
import { AppContext } from "@/stores/appStore";

import Header from "../Header";

const devOrigins = ["https://0.0.0.0:8000", "https://dev.cwb.it.a2z.com:8000"];

/**
 * The Day1 OnBoarding Page which contains different steps to help new hires setting up their laptops.
 *
 * TODO: Get the current progress and determine what to display to the user based on the progress. Keeping this here as POC for using API.
 */
export default function Home(): JSX.Element {
  const intl = useIntl();
  const currentLocale = useSelectedLanguage();
  const { setIsRTL } = useStencilContext();
  const isRTL = RTL_LANGUAGES.includes(currentLocale);
  const { state } = useContext(AppContext);

  useEffect(() => {
    setIsRTL(isRTL);
  }, [isRTL, setIsRTL]);

  useEffect(() => {
    if (
      // If window.opener is not null, it indicates this is a popup because the info in window.opener contains info on the parent who opened it.
      window.opener &&
      // Don't do this in development because it's irritating.
      !devOrigins.includes(window.origin)
    ) {
      window.resizeTo(window.screen.availWidth, window.screen.availHeight);
    }

    document.title = intl.formatMessage({ id: "page-title" });
    document.documentElement.lang = intl.locale;
  }, [intl]);

  const flyoverPad = state.chatSupportFlyoutVisible
    ? { paddingRight: "600px" }
    : {};

  return (
    <>
      {/*Mount Chat Support flyout component in the DOM to allow it to be "resumed" instead of un/re loading the component every time it becomes invisible.*/}
      <ChatSupportFlyout />
      {/*Mount the chatbot flyout component in the DOM*/}
      <ChatbotFlyout />
      {/*Mount the Get Support flyout component in the DOM*/}
      <GetSupportFlyout />
      <Container isFullWidth={true} isCentered={true} style={flyoverPad}>
        <Header />
        <PageContainer>
          <OnboardingPageLayout rail={<ProgressRail />}>
            <H1>
              <FormattedMessage id="page-heading" />
            </H1>
            <Spacer height="S500" />
            <HomeContent />
          </OnboardingPageLayout>
        </PageContainer>
      </Container>
    </>
  );
}
